import { c as _c } from "react/compiler-runtime";
import { sendGTMEvent } from '@next/third-parties/google';
import { keysOf, stringify } from 'betterplace-utils';
import { useTranslations } from 'next-intl';
import analytics, { convertCentsToEuro, getDonationFormType } from 'product-analytics';
import { useCallback } from 'react';
import { usePaymentMethodsToRender } from '../../NativePaymentMethods';
import useFinalizeHandler from './useFinalizeHandler';
import useTrackDonationFormVisit from './useTrackDonationFormVisit';
import { useCaptchaContext } from '@/components/Captcha';
import type { DonationFormValues } from '@/donationForm/types';
import { useErrorHandler } from '@/donationPages/_dependencies/helpers';
import { processDonationFormAction } from '@/donationPages/_dependencies/operations/processDonationAction';
import { donationFormSchema, getDonationFormErrorMap } from '@/donationPages/_dependencies/schemas';
import { useActionForm } from '@/form/ActionForm';
import type { ErrorHandler } from '@/form/ActionForm/useActionForm';
import type { PaymentMethods } from 'api-graphql-types';
function useDonationForm(defaultValues, id) {
  const $ = _c(21);
  const t = useTranslations();
  const {
    paymentMethodsToRender
  } = usePaymentMethodsToRender();
  useTrackDonationFormVisit();
  const {
    solve,
    reset
  } = useCaptchaContext();
  let t0;
  if ($[0] !== solve) {
    t0 = async () => {
      await solve();
    };
    $[0] = solve;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const beforeFormSubmit = t0;
  let t1;
  if ($[2] !== paymentMethodsToRender) {
    t1 = t2 => {
      const {
        payment_method,
        amount_cents,
        interval,
        show_amount,
        show_user,
        show_codonation,
        codonation_cents,
        display_name,
        receipt_desired,
        newsletter,
        message,
        receiver_id,
        receiver_type,
        channel
      } = t2;
      analytics.donationSubmitted({
        donation_amount: convertCentsToEuro(amount_cents),
        recurring_donation: interval,
        payment_method,
        codonation_amount: convertCentsToEuro(codonation_cents),
        show_amount_selected: show_amount,
        show_name_selected: !!(show_user || display_name),
        donation_receipt_requested: receipt_desired,
        platform_newsletter_selected: newsletter,
        comment_added: !!message,
        id: receiver_id,
        receiver_type,
        available_payment_methods: paymentMethodsToRender as PaymentMethods[],
        donation_form_type: getDonationFormType({
          channel,
          receiverId: receiver_id,
          receiverType: receiver_type,
          showCodonation: show_codonation
        })
      });
      sendGTMEvent({
        event: "eeCheckout",
        ecommerce: {
          currencyCode: "EUR",
          checkout: {
            actionField: {
              step: 2,
              option: payment_method
            }
          }
        }
      });
    };
    $[2] = paymentMethodsToRender;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const afterClientSubmitSuccess = t1;
  const afterSubmitSuccess = useFinalizeHandler(t);
  const handleServerErrors = useErrorHandler();
  let t2;
  if ($[4] !== handleServerErrors || $[5] !== reset) {
    t2 = async (errors, values, tools, options) => {
      await handleServerErrors(errors, values, tools, options);
      const {
        _captcha_complete,
        ...rest
      } = errors;
      if (!Object.keys(rest).length) {
        return;
      }
      reset();
    };
    $[4] = handleServerErrors;
    $[5] = reset;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const afterSubmitError = t2;
  let t3;
  if ($[7] !== paymentMethodsToRender) {
    t3 = (errors_0, values_0, tools_0) => {
      for (const key of keysOf(errors_0)) {
        if (key === "root") {
          continue;
        }
        if (key === "") {
          continue;
        }
        tools_0.setValue(key, values_0[key], {
          shouldTouch: true
        });
      }
      const {
        receiver_id: receiverId,
        receiver_type: receiverType,
        channel: channel_0,
        show_codonation: showCodonation
      } = values_0;
      analytics.donationErrorMessageDisplayed({
        donation_form_type: getDonationFormType({
          receiverId,
          receiverType,
          channel: channel_0,
          showCodonation
        }),
        available_payment_methods: paymentMethodsToRender as PaymentMethods[],
        id: receiverId,
        receiver_type: receiverType,
        error: stringify(errors_0),
        errors: errors_0
      });
    };
    $[7] = paymentMethodsToRender;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const afterClientSubmitError = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      returnToOnAuthErrorUrl: ""
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("nextjs.errors.messages.default");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== afterClientSubmitError || $[13] !== afterClientSubmitSuccess || $[14] !== afterSubmitError || $[15] !== afterSubmitSuccess || $[16] !== beforeFormSubmit || $[17] !== defaultValues || $[18] !== id || $[19] !== t5) {
    t6 = {
      action: processDonationFormAction,
      actionContext: t4,
      schema: donationFormSchema,
      getErrorMap: getDonationFormErrorMap,
      defaultValues,
      defaultErrorMessage: t5,
      afterClientSubmitSuccess,
      afterClientSubmitError,
      afterSubmitSuccess,
      afterSubmitError,
      beforeFormSubmit,
      mode: "all",
      reValidateMode: "onChange",
      id
    };
    $[12] = afterClientSubmitError;
    $[13] = afterClientSubmitSuccess;
    $[14] = afterSubmitError;
    $[15] = afterSubmitSuccess;
    $[16] = beforeFormSubmit;
    $[17] = defaultValues;
    $[18] = id;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const form = useActionForm(t6);
  return form;
}
export default useDonationForm;